/** Every relation a member can carry. "Dependent Adult" arrives from Skynet and from rows migrated from WebMUGs only. */
export const RELATIONS = ["Employee", "Spouse", "Child", "Dependent Adult"] as const;
export type Relation = (typeof RELATIONS)[number];

/** The relations an underwriter may give a member on the form; a member who already is a Dependent Adult may stay one. */
export const SELECTABLE_RELATIONS = ["Employee", "Spouse", "Child"] as const satisfies readonly Relation[];

export const GENDERS = ["M", "F"] as const;
export type Gender = (typeof GENDERS)[number];

export const IHQ_TYPES = ["Long", "Short"] as const;
export type IhqType = (typeof IHQ_TYPES)[number];

/** A group's form type may be Unknown when it was migrated without one; the math uses the Long table until an underwriter sets it. */
export const GROUP_IHQ_TYPES = [...IHQ_TYPES, "Unknown"] as const;
export type GroupIhqType = (typeof GROUP_IHQ_TYPES)[number];

/** Who wrote the version: Skynet only for the version Skynet published; the working copy, later copies, and hand-made groups are UW. */
export const CREATED_PROCESSES = ["Skynet", "UW"] as const;
export type CreatedProcess = (typeof CREATED_PROCESSES)[number];

/** The Expected Debits form a group rates against: Unknown falls back to Long. */
export function formTypeFor(ihqType: GroupIhqType): IhqType {
  return ihqType === "Unknown" ? "Long" : ihqType;
}

/**
 * Workbench states are the Salesforce Quote Status values they correspond to
 * (QuoteStatus standard value set), so the group always reads the same stage
 * Sales sees on the quote record. Older versions are not a status: a group that a
 * newer version replaced carries `supersededByGroupId`.
 */
export const GROUP_STATUSES = ["Ready for UW PreScreen", "AI First Initial Score Completed", "PreScreen - WIP - Working", "Preliminary Prescreen Completed", "Renewal Ready"] as const;
export type GroupStatus = (typeof GROUP_STATUSES)[number];

/** Stages a group can sit in before any underwriter work has happened. */
export const UNWORKED_GROUP_STATUSES: readonly GroupStatus[] = ["Ready for UW PreScreen", "AI First Initial Score Completed", "Renewal Ready"];
export const WORKING_GROUP_STATUS: GroupStatus = "PreScreen - WIP - Working";
export const COMPLETED_GROUP_STATUS: GroupStatus = "Preliminary Prescreen Completed";

/**
 * Workbench Status, as the grids and pills say it (feedback 1.6). The value itself is the Salesforce Quote Status wording the
 * stage is written to, and stays in the tooltip so Sales and underwriting can match them up.
 */
export const GROUP_STATUS_SHORT_LABELS: Record<GroupStatus, string> = {
  "Ready for UW PreScreen": "Ready",
  "AI First Initial Score Completed": "Skynet scored",
  "PreScreen - WIP - Working": "Working",
  "Preliminary Prescreen Completed": "Sent to Sales",
  "Renewal Ready": "Renewal ready",
};

export const MEMBER_TYPES = ["Real", "SpockCohort", "GradientDoe"] as const;
export type MemberType = (typeof MEMBER_TYPES)[number];

export const RATING_SOURCES = ["Skynet", "Manual", "Comorbidity", "DoubledBuild", "DoubledHypertension", "BrandToGeneric", "Renewal"] as const;
export type RatingSource = (typeof RATING_SOURCES)[number];

/** Synthetic rows are maintained by the calculation engine rather than chosen by a person. */
export const SYNTHETIC_RATING_SOURCES = ["DoubledBuild", "DoubledHypertension"] as const;
export type SyntheticRatingSource = (typeof SYNTHETIC_RATING_SOURCES)[number];

export const CHANGE_REASONS = ["IRIX Sourced", "Skynet Ignored", "Skynet Hallucinated", "Skynet Error"] as const;
export type ChangeReason = (typeof CHANGE_REASONS)[number];

export const USER_ROLES = ["Underwriter", "Actuarial"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const RELATION_CLASSES = ["Applicant", "SpouseOrDependentAdult", "Child"] as const;
export type RelationClass = (typeof RELATION_CLASSES)[number];

export const ALIAS_SOURCES = ["CsrDatabase", "UwList", "Guideline"] as const;
export type AliasSource = (typeof ALIAS_SOURCES)[number];

export const CATALOG_SEARCH_FILTERS = ["All", "Conditions", "Medications"] as const;
export type CatalogSearchFilter = (typeof CATALOG_SEARCH_FILTERS)[number];

export const MATCH_TIERS = ["Exact", "Alias", "Name", "Medication", "Description"] as const;
export type MatchTier = (typeof MATCH_TIERS)[number];

export interface GroupLockState {
  status: GroupStatus;
  supersededByGroupId: string | null;
}

/** A group is read-only once its prescreen is completed or a newer version exists. */
export function isGroupLocked(group: GroupLockState): boolean {
  return group.status === COMPLETED_GROUP_STATUS || group.supersededByGroupId !== null;
}

/** Why a version is locked, or that it is open (feedback 1.3). A locked version never unlocks; Reopen makes a new version. */
export function groupLockReason(group: GroupLockState): string {
  if (group.supersededByGroupId !== null) return "Locked: a newer version replaced this one";
  if (group.status === COMPLETED_GROUP_STATUS) return "Locked: sent to Sales";
  return "Open: this version can be edited";
}
